import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Badge
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import api from '../api';

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const messagesEndRef = useRef(null);

  // Fetch user data on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await api.get('/api/auth/user');
        setUser(response.data);
      } catch (err) {
        console.error('Failed to fetch user:', err);
      }
    };
    fetchUser();
  }, []);



  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      // Don't show loading for background updates
      if (messages.length === 0) {
        setLoading(true);
      }
      
      const response = await api.get('/api/chat/messages');
      
      if (response.data && Array.isArray(response.data)) {
        setMessages(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      if (err.response?.status === 401) {
        setError('Please log in to view chat messages');
      } else {
        setError('Failed to load chat messages');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const response = await api.get('/api/chat/online-users');
      if (response.data && Array.isArray(response.data)) {
        setOnlineUsers(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch online users:', err);
    }
  };

  // Fetch messages on mount and set up polling
  useEffect(() => {
    fetchMessages();
    fetchOnlineUsers();
    
    // Poll for new messages every 15 seconds (increased to reduce interference)
    const messageInterval = setInterval(fetchMessages, 15000);
    const userInterval = setInterval(fetchOnlineUsers, 60000); // Poll users every minute
    
    return () => {
      clearInterval(messageInterval);
      clearInterval(userInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array since functions are defined above

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !user) return;

    try {
      // Don't set global loading for message sending to prevent input interference
      const response = await api.post('/api/chat/messages', {
        text: newMessage.trim(),
        userId: user._id,
        userEmail: user.email
      });

      if (response.data) {
        setMessages(prev => [...prev, response.data]);
        setNewMessage('');
        setError(null);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const isOwnMessage = (messageUserId) => {
    return user && messageUserId === user._id;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Main Chat Area */}
        <Box sx={{ flex: 1 }}>
          <Paper elevation={3} sx={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
            {/* Chat Header */}
            <Box sx={{ 
              p: 2, 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <ChatIcon />
              <Typography variant="h6">Traders Chat</Typography>
              <Chip 
                label={`${onlineUsers.length} online`} 
                size="small" 
                sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            </Box>

            {/* Messages Area */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {loading && messages.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : messages.length === 0 ? (
                <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
                  <ChatIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6">No messages yet</Typography>
                  <Typography variant="body2">Be the first to start the conversation!</Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {messages.map((message, index) => (
                    <ListItem
                      key={message._id || index}
                      sx={{
                        flexDirection: 'column',
                        alignItems: isOwnMessage(message.userId) ? 'flex-end' : 'flex-start',
                        mb: 1,
                        p: 0
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '70%',
                          bgcolor: isOwnMessage(message.userId) ? 'primary.main' : 'grey.100',
                          color: isOwnMessage(message.userId) ? 'white' : 'text.primary',
                          borderRadius: 2,
                          p: 1.5,
                          position: 'relative'
                        }}
                      >
                        {/* Message Header */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          mb: 0.5,
                          opacity: 0.8
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {message.userEmail || 'Anonymous'}
                          </Typography>
                          <Typography variant="caption">
                            {formatTime(message.createdAt)}
                          </Typography>
                        </Box>
                        
                        {/* Message Text */}
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {message.text}
                        </Typography>
                      </Box>
                    </ListItem>
                  ))}
                  <div ref={messagesEndRef} />
                </List>
              )}
            </Box>

            {/* Message Input */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <form onSubmit={sendMessage}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    variant="outlined"
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading || !user}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!newMessage.trim() || loading || !user}
                    sx={{ borderRadius: 3, minWidth: 56 }}
                  >
                    <SendIcon />
                  </Button>
                </Box>
              </form>
            </Box>
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
        </Box>

        {/* Online Users Sidebar */}
        <Box sx={{ width: 280 }}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">Online Traders</Typography>
              <IconButton size="small" onClick={fetchOnlineUsers}>
                <RefreshIcon />
              </IconButton>
            </Box>
            
            <List sx={{ p: 0 }}>
              {onlineUsers.map((onlineUser) => (
                <ListItem key={onlineUser._id} sx={{ p: 1 }}>
                  <ListItemAvatar>
                    <Badge
                      color="success"
                      variant="dot"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                      <Avatar sx={{ width: 32, height: 32 }}>
                        <PersonIcon />
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={onlineUser.email}
                    secondary="Online"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
              
              {onlineUsers.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No users online"
                    secondary="Check back later!"
                    sx={{ textAlign: 'center', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default Chat;